import { useMemo, useState } from 'react';
import { useStore } from '@/store';
import { virtues, flaws } from '@/data';
import type { Virtue, Flaw } from '@/types/data';
import type { CharacterType, ChosenVirtue, ChosenFlaw } from '@/types/character';
import { SearchField } from '@/components/ui/SearchField';
import { InfoHint } from '@/components/ui/InfoHint';

// Archetypally required virtues per character type. These cannot be removed
// from the Chosen panel — removing them would put the character in an
// unsalvageable state (e.g. a magus without The Gift).
const REQUIRED_VIRTUES: Record<CharacterType, Set<string>> = {
  grog: new Set(),
  companion: new Set(),
  magus: new Set(['the-gift', 'hermetic-magus']),
};

function isRequiredVirtue(type: CharacterType, id: string): boolean {
  return REQUIRED_VIRTUES[type].has(id);
}

export function VirtuesFlawsSection() {
  const c = useStore((s) => s.character!);
  const update = useStore((s) => s.updateCharacter);
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState<'all' | 'major' | 'minor' | 'free'>('all');

  const virtueFiltered = useMemo(() => filterEntries(virtues, query, catFilter), [query, catFilter]);
  const flawFiltered = useMemo(() => filterEntries(flaws, query, catFilter), [query, catFilter]);

  const pickedVirtueMap = new Map(c.virtues.map((v) => [v.id, v]));
  const pickedFlawMap = new Map(c.flaws.map((f) => [f.id, f]));

  return (
    <div className="space-y-6">
      <div className="card flex flex-wrap items-center gap-2">
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Search virtues & flaws…"
        />
        <select
          className="input-base w-auto"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value as typeof catFilter)}
        >
          <option value="all">All</option>
          <option value="major">Major</option>
          <option value="minor">Minor</option>
          <option value="free">Free</option>
        </select>
      </div>

      <ChosenPanel
        type={c.type}
        virtues={c.virtues}
        flaws={c.flaws}
        onRemoveVirtue={(id) =>
          update((cur) => ({ ...cur, virtues: cur.virtues.filter((v) => v.id !== id) }))
        }
        onRemoveFlaw={(id) =>
          update((cur) => ({ ...cur, flaws: cur.flaws.filter((f) => f.id !== id) }))
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        <VFColumn
          title="Virtues"
          items={virtueFiltered}
          pickedMap={pickedVirtueMap}
          onToggle={(id) => {
            if (pickedVirtueMap.has(id) && isRequiredVirtue(c.type, id)) return;
            update((cur) => ({
              ...cur,
              virtues: pickedVirtueMap.has(id)
                ? cur.virtues.filter((v) => v.id !== id)
                : [...cur.virtues, { id }],
            }));
          }}
          onChooseCategory={(id, cat) =>
            update((cur) => ({
              ...cur,
              virtues: cur.virtues.map((v) => (v.id === id ? { ...v, chosenCategory: cat } : v)),
            }))
          }
        />
        <VFColumn
          title="Flaws"
          items={flawFiltered}
          pickedMap={pickedFlawMap}
          onToggle={(id) =>
            update((cur) => ({
              ...cur,
              flaws: pickedFlawMap.has(id)
                ? cur.flaws.filter((f) => f.id !== id)
                : [...cur.flaws, { id }],
            }))
          }
          onChooseCategory={(id, cat) =>
            update((cur) => ({
              ...cur,
              flaws: cur.flaws.map((f) => (f.id === id ? { ...f, chosenCategory: cat } : f)),
            }))
          }
        />
      </div>
    </div>
  );
}

function ChosenPanel({
  type,
  virtues: cv,
  flaws: cf,
  onRemoveVirtue,
  onRemoveFlaw,
}: {
  type: CharacterType;
  virtues: ChosenVirtue[];
  flaws: ChosenFlaw[];
  onRemoveVirtue: (id: string) => void;
  onRemoveFlaw: (id: string) => void;
}) {
  const vDefs = (id: string) => virtues.find((x) => x.id === id);
  const fDefs = (id: string) => flaws.find((x) => x.id === id);
  return (
    <section className="grid gap-4 md:grid-cols-2">
      <div>
        <h3 className="section-title">
          Chosen Virtues <span className="text-sm muted">({cv.length})</span>
          <InfoHint>
            <strong>Major = 3 pts, Minor = 1 pt, Free = 0.</strong> Up to{' '}
            {type === 'grog' ? '3' : '10'} points of Virtues, matched one-for-one
            by Flaws (they must balance). Free Virtues like The Gift don't cost
            anything. Items tagged <em>Major or Minor</em> need you to commit to
            one via the "Take as …" buttons.
          </InfoHint>
        </h3>
        {cv.length === 0 && (
          <div className="muted text-sm italic">No virtues yet. Pick from the list below.</div>
        )}
        <ul className="space-y-1">
          {cv.map((v) => {
            const def = vDefs(v.id);
            const required = isRequiredVirtue(type, v.id);
            return (
              <li
                key={v.id}
                className="flex items-center justify-between gap-2 rounded-md border border-gold-400/25 bg-ink-800/40 px-3 py-1.5"
              >
                <span className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{def?.name ?? v.id}</span>
                  {v.chosenCategory && <span className="chip">{v.chosenCategory}</span>}
                  {!v.chosenCategory && def?.category && def.category !== 'either' && (
                    <span className="chip">{def.category}</span>
                  )}
                  {required && <span className="chip">required</span>}
                </span>
                {required ? (
                  <span
                    className="text-xs text-gold-400/60 px-2"
                    title="Required for this character type — cannot be removed"
                    aria-label="required virtue"
                  >
                    🔒
                  </span>
                ) : (
                  <button
                    className="btn-quiet !px-2 !py-0.5 text-xs"
                    onClick={() => onRemoveVirtue(v.id)}
                    aria-label={`Remove ${def?.name ?? v.id}`}
                  >
                    ✕
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div>
        <h3 className="section-title">
          Chosen Flaws <span className="text-sm muted">({cf.length})</span>
          <InfoHint>
            Each Flaw grants the same points its Virtue mirror costs. Limits:
            max 1 Story Flaw, max 2 Personality Flaws, max 5 Minor Flaws{' '}
            {type === 'grog' ? '(grogs: none of the above).' : '(companion/magus).'}
            {type !== 'grog' && ' Magi are expected to have at least one Hermetic Flaw.'}
          </InfoHint>
        </h3>
        {cf.length === 0 && (
          <div className="muted text-sm italic">No flaws yet. Pick from the list below.</div>
        )}
        <ul className="space-y-1">
          {cf.map((f) => {
            const def = fDefs(f.id);
            return (
              <li
                key={f.id}
                className="flex items-center justify-between gap-2 rounded-md border border-gold-400/25 bg-ink-800/40 px-3 py-1.5"
              >
                <span className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{def?.name ?? f.id}</span>
                  {f.chosenCategory && <span className="chip">{f.chosenCategory}</span>}
                  {!f.chosenCategory && def?.category && def.category !== 'either' && (
                    <span className="chip">{def.category}</span>
                  )}
                </span>
                <button
                  className="btn-quiet !px-2 !py-0.5 text-xs"
                  onClick={() => onRemoveFlaw(f.id)}
                >
                  ✕
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function filterEntries<T extends Virtue | Flaw>(
  all: T[],
  query: string,
  cat: 'all' | 'major' | 'minor' | 'free'
): T[] {
  const q = query.trim().toLowerCase();
  return all.filter((e) => {
    if (cat !== 'all' && e.category !== cat && e.category !== 'either') return false;
    if (!q) return true;
    return (
      e.name.toLowerCase().includes(q) ||
      (e.subtype ?? []).some((s: string) => s.toLowerCase().includes(q))
    );
  });
}

function VFColumn<T extends Virtue | Flaw>({
  title,
  items,
  pickedMap,
  onToggle,
  onChooseCategory,
}: {
  title: string;
  items: T[];
  pickedMap: Map<string, { id: string; chosenCategory?: 'major' | 'minor' }>;
  onToggle: (id: string) => void;
  onChooseCategory: (id: string, cat: 'major' | 'minor') => void;
}) {
  return (
    <div>
      <h3 className="section-title">
        {title} <span className="text-sm muted">({items.length})</span>
      </h3>
      <div className="max-h-[540px] overflow-y-auto space-y-1 pr-1 ars-scrollbar">
        {items.map((e) => {
          const pick = pickedMap.get(e.id);
          const picked = !!pick;
          const needsChoice = picked && e.category === 'either' && !pick?.chosenCategory;
          return (
            <div
              key={e.source}
              className={`rounded-md border transition-colors ${
                picked
                  ? needsChoice
                    ? 'border-amber-400/40 bg-amber-400/5'
                    : 'border-gold-400/60 bg-gold-400/10'
                  : 'border-ink-700 hover:border-gold-400/40'
              }`}
            >
              <button onClick={() => onToggle(e.id)} className="w-full text-left px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{e.name}</span>
                  <span className="flex gap-1">
                    {e.category && (
                      <span className="chip capitalize">
                        {e.category === 'either' ? 'Major or Minor' : e.category}
                      </span>
                    )}
                    {(e.subtype ?? []).slice(0, 2).map((s) => (
                      <span key={s} className="chip capitalize">
                        {s}
                      </span>
                    ))}
                  </span>
                </div>
              </button>
              {picked && e.category === 'either' && (
                <div className="flex gap-2 px-3 pb-2 text-xs">
                  <button
                    className={`btn ${
                      pick?.chosenCategory === 'minor' ? 'btn-primary' : 'btn-secondary'
                    } !px-2 !py-1`}
                    onClick={() => onChooseCategory(e.id, 'minor')}
                  >
                    Take as Minor
                  </button>
                  <button
                    className={`btn ${
                      pick?.chosenCategory === 'major' ? 'btn-primary' : 'btn-secondary'
                    } !px-2 !py-1`}
                    onClick={() => onChooseCategory(e.id, 'major')}
                  >
                    Take as Major
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
